import { SbbAutocomplete } from "@sbb-esta/lyne-react/autocomplete";
import { SbbOption } from "@sbb-esta/lyne-react/option";
import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import { defaultSize } from "components/Lyne/LyneConstants";
import { LyneFormField } from "components/Lyne/LyneFormField";
import { createLogger } from "logging";
import { StringChoiceModel } from "models/ChoiceModel";
import { getResourceStrict } from "providers";
import { useRef, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { HookFormError } from "./HookFormError";

const logger = createLogger("HookForm:ComboBox");

interface HookFormComboBoxProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  maxLength?: number;
  size?: "s" | "m" | "l";
  options: StringChoiceModel[];
  placeholder?: string;
  minInputLength?: number;
}

export const HookFormComboBox = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormComboBoxProps<TFieldValues, TName>
) => {
  const {
    label,
    name,
    control,
    maxLength,
    size = defaultSize,
    options,
    placeholder,
    minInputLength = 1,
    contextHelp,
    required,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const labelText = label ? getResourceStrict(label) : undefined;

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, fieldState }) => {
        const { onChange, value, ref, onBlur, disabled } = field;
        const { isDirty, isTouched, error } = fieldState;

        const className = classNames(
          isDirty ? "is-dirty" : "",
          isTouched ? "is-touched" : "",
          error ? "is-error" : "",
          required && (isTouched || isDirty) && !value ? "sbb-invalid" : ""
        );

        // Filter options based on current input
        const showSuggestions = inputValue.length >= minInputLength;
        const filteredOptions = showSuggestions
          ? options.filter((option) => option.text.toLowerCase().includes(inputValue.toLowerCase()))
          : [];

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          logger.debug(`${name}.handleInputChange(): `, newValue);
          setInputValue(newValue);
          onChange(newValue);
        };

        const handleOptionSelected = (option: StringChoiceModel) => {
          logger.debug(`${name}.handleOptionSelected(): `, option);
          const newValue = option.value;
          onChange(newValue);
          setInputValue(newValue);
          if (inputRef.current) {
            inputRef.current.value = newValue;
          }
        };

        // Sync input ref with form value when mounting or value changes
        const currentValue = (value as string) || "";
        if (inputRef.current && inputRef.current.value !== currentValue && !inputValue) {
          inputRef.current.value = currentValue;
        }

        return (
          <LyneFormField name={name} size={size} className="combo-box" contextHelp={contextHelp} label={label}>
            <input
              ref={(e) => {
                ref(e);
                if (e) {
                  inputRef.current = e;
                }
              }}
              className={className}
              name={name}
              defaultValue={currentValue}
              disabled={disabled}
              maxLength={maxLength}
              autoComplete="off"
              placeholder={placeholder || labelText}
              onBlur={(e) => {
                logger.debug(`${name}.onBlur(): `, e.target.value);
                onBlur();
              }}
              onChange={handleInputChange}
            />
            {filteredOptions.length > 0 && showSuggestions && (
              <SbbAutocomplete>
                {filteredOptions.map((option) => {
                  const optionValue = option.value;
                  const key = `${name}-${option.text}`;

                  return (
                    <SbbOption
                      key={key}
                      value={optionValue}
                      disabled={option.disabled}
                      onOptionselected={() => handleOptionSelected(option)}
                    >
                      {option.text}
                    </SbbOption>
                  );
                })}
              </SbbAutocomplete>
            )}
            <HookFormError error={error} />
          </LyneFormField>
        );
      }}
    />
  );
};
